import { useState, useEffect } from "react";

import { useNavigate } from "react-router-dom";

import { DoLogin } from "@/services/Login";
import { GetCurrentUser } from "@/services/Setting";
import useUserStore from "@/stores/User";
import { clearAllCookies, clearBrowserStorage } from "@/utils/cookieUtils";
import {
  clearReturnUrl,
  getReturnUrl,
  restoreReturnUrl,
} from "@/utils/returnUrlUtils";

import CommonButton from "@/components/Common/Button";
import CommonInput from "@/components/Common/Input";
import CommonImg from "@/components/Common/Img";

import ThermoFisher from "@/assets/images/thermo-fisher-logo.png";

export default function Login() {
  const navigate = useNavigate();
  const setUser = useUserStore((state) => state.setUser);

  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const companyId = "cbed5e25-3d2c-41b2-b9e3-44f550c7fc2f";

  // 로그인 성공 시 원래 가려던 페이지로 복귀 (없으면 기본 목록)
  const redirectAfterLogin = () => {
    const returnUrl = getReturnUrl();
    clearReturnUrl();
    navigate(returnUrl ?? "/bid/domestic/list", { replace: true });
  };

  // SSO 로그인 후 사용자 정보 가져오기
  const handleSsoLoginResult = async () => {
    try {
      const userResult = await GetCurrentUser();
      if (userResult.data.success && userResult.data.pods[0]?.content) {
        setUser(userResult.data.pods[0].content);
        redirectAfterLogin();
      } else {
        // SSO 로그인 실패 시 쿠키 정리
        clearAllCookies();
        clearBrowserStorage();
        alert("SSO 로그인 후 사용자 정보를 가져올 수 없습니다.");
      }
    } catch (error) {
      console.error("Failed to fetch current user after SSO login:", error);
      // SSO 로그인 실패 시 쿠키 정리
      clearAllCookies();
      clearBrowserStorage();
      alert("SSO 로그인 후 사용자 정보를 가져오는데 실패했습니다.");
    }
  };

  const handleClickLogin = async () => {
    try {
      const params = `auth.sign_in_by_password(company_id=\"${companyId}\", email=\"${email}\", password=\"${password}\")`;
      const result = await DoLogin(params);

      if (result.data.success) {
        // 현재 사용자 정보 가져와서 스토어에 저장
        try {
          const userResult = await GetCurrentUser();
          if (userResult.data.success && userResult.data.pods[0]?.content) {
            setUser(userResult.data.pods[0].content);
          }
        } catch (userError) {
          console.error("현재 사용자 정보 가져오기 실패 : ", userError);
        }

        redirectAfterLogin();
      } else {
        alert(`로그인 실패 ${result.data.error.content}`);
      }
    } catch (e) {
    } finally {
    }
  };

  const handleClickLoginWithSso = async () => {
    try {
      // window.location.href = "https://api.uat.gtpa-thermofisher.com/sso/login";
      window.location.href = process.env.REACT_APP_SSO_URL ?? "";
    } catch (e) {
    } finally {
    }
  };

  useEffect(() => {
    // 로그인 페이지 진입 시 기존 세션 정리
    // (returnUrl은 스토리지 초기화 전에 백업했다가 복원 — SSO 왕복에도 유지)
    const returnUrl = getReturnUrl();
    clearAllCookies();
    clearBrowserStorage();
    restoreReturnUrl(returnUrl);

    const queryParams = new URLSearchParams(window.location.search);
    if (queryParams.get("not_found") === "true") {
      console.error("해당 유저 GTPA DB에 없음");
    } else if (queryParams.get("success") === "true") {
      handleSsoLoginResult();
    }
  }, []);

  return (
    <div className="h-screen flex justify-center items-center">
      <div className="w-[200px] flex flex-col gap-[14px]">
        <CommonImg
          className="w-full h-[40px]"
          src={ThermoFisher}
          alt="Thermofisher Logo"
        />
        <div className="border-gray-E border-[1px]" />
        <CommonButton
          className="w-full text-sm"
          label={"Login with SSO"}
          onClick={handleClickLoginWithSso}
        />
        <div className="border-gray-D border-[1px]" />
        <div className="flex flex-col gap-[8px]">
          <div className="text-gray-B text-xs font-bold">E-mail</div>
          <CommonInput
            className="w-[200px]"
            defaultValue={email}
            onChange={(value) => setEmail(value)}
            onKeyDownEnter={handleClickLogin}
          />
        </div>
        <div className="flex flex-col gap-[8px]">
          <div className="text-gray-B text-xs font-bold">Password</div>
          <CommonInput
            type="password"
            className="w-[200px]"
            defaultValue={password}
            onChange={(value) => setPassword(value)}
            onKeyDownEnter={handleClickLogin}
          />
        </div>
        <div className="flex justify-center">
          <CommonButton label="Login" onClick={handleClickLogin} />
        </div>
      </div>
    </div>
  );
}
